import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SpiceLevel from '../SpiceLevel';
import './index.css';
import { getTenantId } from '../../config/tenantConfig';
import apiCache from '../../utils/apiCache';
import { useOrderFlow } from '../../context/OrderFlowContext';
import { checkStoreAvailability, getStoreStatus } from '../checkStoreAvailability';
import { settingsService } from '../../config/settingsService';
import { getValidAuthSession } from '../../utils/authStorage';
import AuthModal from '../AuthModal';
import StoreClosedModal from '../StoreClosedModal';
import FallbackImg from '../FallbackImg';
import { useSpecialtyCart, SpecialtyCartModals } from '../../hooks/useSpecialtyCart';

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

// Same guard RestaurantMenuApp's own ProductCard uses
// (product.isVeg !== undefined) — items whose source data never carried
// veg/non-veg info (e.g. the static config fallback below) render nothing,
// rather than a misleading dot. One shared component so every template's
// card below reuses the exact same markup/classes instead of repeating it.
const VegNonVegBadge = ({ isVeg }) => {
  if (isVeg === undefined) return null;
  return (
    <div className="special-menu__veg-badge-wrap">
      <div className="special-menu__veg-badge">
        <div className={isVeg ? 'special-menu__veg-dot' : 'special-menu__non-veg-dot'}></div>
      </div>
    </div>
  );
};

// Same fallback used by WeightSelectionModal (the shop page's real weight
// picker) when a product's own weight_config doesn't list explicit options —
// reusing that existing convention rather than inventing a new one.
const DEFAULT_WEIGHT_OPTIONS = [
  { label: '250g', value: 0.25 },
  { label: '500g', value: 0.5 },
  { label: '1 kg', value: 1 },
];

// Mirrors RestaurantMenuApp's own cart storage — used only by
// SweetStoreSpecialMenu (Template 16) below, which keeps its own original,
// untouched cart-write implementation (every other template's component
// instead calls the shared useSpecialtyCart hook, which has its own copy of
// this same constant).
const CART_STORAGE_KEY = 'resto_cart_data';
const getCartKey = (storeId, orderType) => `${CART_STORAGE_KEY}_${storeId}_${orderType}`;

// ── Live cart quantities for SweetStoreSpecialMenu (Template 16) ──
// Module-level, not component state — it must survive SweetStoreSpecialMenu
// unmounting (e.g. the user navigates to the Restaurant Menu App, changes a
// quantity there, then comes back), so that change is still reflected once
// this section remounts. It must NOT survive an actual page reload though —
// showing a stale count from a much earlier browsing session the instant a
// fresh visit loads is exactly the confusing behavior the "this card
// intentionally does NOT read live cart quantities" comment below was
// originally written to avoid. A plain module-level object gives us both:
// it starts empty on every real page load (fresh JS module evaluation) but
// persists across client-side route changes within that same load. It is
// only ever written by refreshLiveCartQuantities(), in response to a real
// cart_updated/storage event — never read from localStorage eagerly.
let liveCartQuantities = {};
let lastCartContext = null; // { storeId, orderType } — set by any mounted instance

const refreshLiveCartQuantities = () => {
  if (!lastCartContext?.storeId) return;
  const key = getCartKey(lastCartContext.storeId, lastCartContext.orderType);
  try {
    const stored = JSON.parse(localStorage.getItem(key) || 'null');
    const items = stored?.items || [];
    const map = {};
    items.forEach(it => {
      if (it.cartItemKey) map[it.cartItemKey] = (map[it.cartItemKey] || 0) + (it.quantity || 0);
    });
    liveCartQuantities = map;
  } catch {
    liveCartQuantities = {};
  }
};

// Attached exactly once per page load (idempotent), independent of any one
// component instance's mount/unmount, so liveCartQuantities keeps updating
// in the background even while SweetStoreSpecialMenu isn't mounted.
let liveCartListenersAttached = false;
const ensureLiveCartListeners = () => {
  if (liveCartListenersAttached || typeof window === 'undefined') return;
  liveCartListenersAttached = true;
  window.addEventListener('cart_updated', refreshLiveCartQuantities);
  window.addEventListener('storage', refreshLiveCartQuantities);
};

const SpecialMenu = ({ config, nearestStore }) => {
  // Used by the base fallback render below (Template 1 "Modern", and
  // Template 15 "Dry Fruits" which has no dedicated case and falls through
  // to the same fallback) — the only two templates with no per-template
  // component of their own. Every other template calls this same hook from
  // inside its own component instead. Must be called unconditionally here,
  // before any of this component's early returns.
  const fallbackCart = useSpecialtyCart(nearestStore);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(4); // Changed to 4 for desktop matching sample image
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const nearestStoreIdRef = useRef(null);
  const sectionRef = useRef(null);

  const { specialMenu } = config;

  // Fetch popular items
  useEffect(() => {
    const branchId = nearestStore?.id ?? null;

    // Check cache first to prevent redundant network calls
    const cachedData = apiCache.getPopularItems(branchId);
    if (cachedData && cachedData.length > 0) {
      setMenuItems(cachedData);
      setLoading(false);
      setHasFetched(true);
      return;
    }

    // If we've already fetched for this branch and have items, don't re-fetch unless requested
    if (hasFetched && menuItems.length > 0 && nearestStoreIdRef.current === branchId) {
      return;
    }

    const fetchPopularItems = async () => {
      try {
        // Only show loading if we don't have items yet to prevent flickering
        if (menuItems.length === 0) {
          setLoading(true);
        }

        const url = branchId
          ? `${API_BASE}/web/item/popular-items?branch_id=${branchId}&limit=12`
          : `${API_BASE}/web/item/popular-items?limit=12`;
        const commonHeaders = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Api-Key': X_API_KEY,
          'X-Tenant': getTenantId()
        };

        // Popular items only carry item_category_id (a number), not a
        // resolved name — reuse the shop page's own category list/cache to
        // turn that id into the real category name shown on each card.
        const cachedCategories = apiCache.getCategories();
        const categoriesPromise = cachedCategories
          ? Promise.resolve(cachedCategories)
          : fetch(`${API_BASE}/web/item-category`, { headers: commonHeaders })
            .then(res => res.ok ? res.json() : Promise.reject(new Error('Failed to fetch categories')))
            .then(data => {
              const categories = data.data || [];
              apiCache.setCategories(categories);
              return categories;
            })
            .catch(() => []);

        const response = await fetch(url, { headers: commonHeaders });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const result = await response.json();
        const allItems = result?.data || [];
        const categories = await categoriesPromise;
        const categoryNameById = {};
        categories.forEach(cat => { categoryNameById[String(cat.id)] = cat.name; });

        const mappedPopular = Array.isArray(allItems) ? allItems.map(item => {
          const isWeightBased = item.isWeightBased || item.unit_type === 2 || item.weight_config?.is_weight_based === true;
          const weightUnitLabel = item.weight_config?.unit || item.unit || 'kg';
          const weightOptions = isWeightBased
            ? (item.weight_config?.weight_options?.length > 0
              ? item.weight_config.weight_options.map(o => ({
                label: o.label || `${o.value} ${weightUnitLabel}`,
                value: parseFloat(o.value)
              }))
              : DEFAULT_WEIGHT_OPTIONS)
            : [];

          return {
            id: item.id,
            title: item.name || item.title,
            description: item.description || '',
            image: item.preview || item.cover || item.thumb || item.image || item.image_url || '',
            price: item.currency_price || item.price,
            // Numeric price for math (weight calculations, cart amounts) —
            // item.currency_price/item.price above is a pre-formatted
            // "₹300.00" display string that parseFloat can't read, which
            // was silently producing ₹0.00 everywhere it got multiplied.
            // Kept as a separate field so every other template's price
            // display (which reads item.price as-is) is untouched.
            priceValue: parseFloat(item.price) || parseFloat(item.flat_price) || item.convert_price || 0,
            categoryName: categoryNameById[String(item.item_category_id)] || '',
            // Same field/derivation RestaurantMenuApp's loadInitialData uses
            // (isVeg: product.item_type === 5) — no new veg/non-veg logic.
            isVeg: item.item_type === 5,
            spiceLevel: item.spice_level || 0, // 0-5; 0 shows nothing
            isWeightBased,
            weightUnitLabel,
            weightOptions,
            isTracked: item.is_tracked || false,
            isAvailable: item.is_available !== false
          };
        }) : [];

        if (mappedPopular.length > 0) {
          apiCache.setPopularItems(branchId, mappedPopular);
          setMenuItems(mappedPopular);
        } else if (menuItems.length === 0) {
          // Only fallback to config if we have absolutely nothing
          const mappedSample = (Array.isArray(specialMenu?.items) ? specialMenu.items : []).map(item => ({
            id: item.id,
            title: item.name || item.title,
            description: item.description || '',
            image: item.image || item.preview,
            price: item.price
          }));
          setMenuItems(mappedSample);
        }

        nearestStoreIdRef.current = branchId;
        setHasFetched(true);
      } catch (err) {
        console.error("❌ Special Menu fetch failed:", err.message);
        if (menuItems.length === 0) {
          const mappedSample = (Array.isArray(specialMenu?.items) ? specialMenu.items : []).map(item => ({
            id: item.id,
            title: item.name || item.title,
            description: item.description || '',
            image: item.image || item.preview,
            price: item.price
          }));
          setMenuItems(mappedSample);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPopularItems();
  }, [nearestStore?.id, JSON.stringify(specialMenu)]);

  // Update slides count based on screen size
  const updateItemsCount = () => {
    setCurrentSlide(0);
    if (window.innerWidth <= 480) {
      setItemsPerSlide(1);
    } else if (window.innerWidth <= 768) {
      setItemsPerSlide(2);
    } else if (window.innerWidth <= 1024) {
      setItemsPerSlide(3);
    } else {
      setItemsPerSlide(4);
    }
  };

  useEffect(() => {
    updateItemsCount();
    window.addEventListener('resize', updateItemsCount);
    return () => window.removeEventListener('resize', updateItemsCount);
  }, []);

  const totalSlides = Math.ceil(menuItems.length / itemsPerSlide) || 1;

  // Auto-advance carousel
  useEffect(() => {
    if (menuItems.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % totalSlides);
    }, 4000); // 4 seconds
    return () => clearInterval(interval);
  }, [totalSlides, menuItems.length]);

  const goToSlide = (i) => setCurrentSlide(i);

  // Group items into slides
  const slides = [];
  for (let i = 0; i < totalSlides; i++) {
    const start = i * itemsPerSlide;
    slides.push(menuItems.slice(start, start + itemsPerSlide));
  }

  const getFlexBasis = () => {
    if (itemsPerSlide === 1) return '100%';
    if (itemsPerSlide === 2) return 'calc(50% - 15px)';
    if (itemsPerSlide === 3) return 'calc(33.333% - 20px)';
    return 'calc(25% - 22.5px)'; // 4 items
  };

  const type = (config.templateType || 'template1').toLowerCase().replace(/-/g, '_');
  const sectionHeading = specialMenu?.heading || "Our Specialties";
  const sectionDesc = specialMenu?.sub_heading || "";

  if (loading) {
    return (
      <div className="popular-items-section pad-top-100 pad-bottom-100">
        <div className="container text-center">
          <div className="popular-loading">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-3">Loading specialties...</p>
          </div>
        </div>
      </div>
    );
  }

  const displayItems = menuItems;

  switch (type) {
    case 'template1':
    case 'modern':
      break;
    case 'template2':
    case 'classic':
      return <ClassicSpecialMenu config={config} menuItems={displayItems} loading={loading} nearestStore={nearestStore} />;
    case 'template3':
    case 'traditional':
      return <TraditionalSpecialMenu config={config} menuItems={displayItems} loading={loading} nearestStore={nearestStore} />;
    case 'template4':
    case 'asha_legacy':
      return <LegacyAshaSpecialMenu config={config} menuItems={displayItems} loading={loading} nearestStore={nearestStore} />;
    case 'template5':
    case 'apparel':
      return <ApparelSpecialMenu config={config} menuItems={displayItems} loading={loading} nearestStore={nearestStore} />;
    case 'template6':
    case 'luxury_restaurant':
      return <LuxurySpecialMenu config={config} menuItems={displayItems} loading={loading} nearestStore={nearestStore} />;
    case 'template7':
    case 'sweet_shop':
      return <SweetShopSpecialMenu config={config} menuItems={displayItems} loading={loading} nearestStore={nearestStore} />;
    case 'template8':
    case 'heritage_royal':
      return <HeritageSpecialMenu config={config} menuItems={displayItems} loading={loading} nearestStore={nearestStore} />;
    case 'template9':
    case 'modern_mithai':
      return <ModernMithaiSpecialMenu config={config} menuItems={displayItems} loading={loading} nearestStore={nearestStore} />;
    case 'template10':
    case 'festive_sweets':
      return <FestiveSweetsSpecialMenu config={config} menuItems={displayItems} loading={loading} nearestStore={nearestStore} />;
    case 'template11':
    case 'royal_confections':
      return <RoyalConfectionsSpecialMenu config={config} menuItems={displayItems} loading={loading} nearestStore={nearestStore} />;
    case 'template12':
    case 'traditional_sweets':
      return <TraditionalSweetsSpecialMenu config={config} menuItems={displayItems} loading={loading} nearestStore={nearestStore} />;
    case 'template13':
    case 'sunburst_gourmet':
      return <SunburstSpecialMenu config={config} menuItems={displayItems} loading={loading} nearestStore={nearestStore} />;
    case 'template14':
    case 'cafe_premium':
      return <CafeSpecialMenu config={config} menuItems={displayItems} loading={loading} nearestStore={nearestStore} />;
    case 'template16':
    case 'arisa_collection':
      return <SweetStoreSpecialMenu config={config} menuItems={displayItems} loading={loading} nearestStore={nearestStore} />;
  }

  return (
    <div className="popular-items-section pad-top-100 pad-bottom-100" ref={sectionRef}>
      <div className="container">
        {/* Section Header */}
        <div className="popular-header text-center wow fadeIn" data-wow-duration="1s" data-wow-delay="0.1s">
          <h2 className="popular-title">{sectionHeading}</h2>
          <p className="popular-caption">{sectionDesc}</p>
          <div className="modern-testimonials__title-line" />

        </div>

        <div className="popular-carousel-wrapper">
          <div className="popular-carousel">
            <div className="popular-slides-container">
              {slides.map((slideItems, slideIndex) => (
                <div
                  key={slideIndex}
                  className="popular-slide"
                  style={{
                    position: slideIndex === 0 ? 'relative' : 'absolute',
                    opacity: currentSlide === slideIndex ? 1 : 0,
                    pointerEvents: currentSlide === slideIndex ? 'auto' : 'none',
                  }}
                >
                  {slideItems.map((item, itemIndex) => (
                    <div
                      key={`${slideIndex}-${itemIndex}`}
                      className="popular-card"
                      style={{
                        flex: `0 0 ${getFlexBasis()}`,
                        maxWidth: getFlexBasis()
                      }}
                    >
                      <div className="popular-card__img-box">
                        <VegNonVegBadge isVeg={item.isVeg} />
                        <FallbackImg
                          src={item.image}
                          alt={item.title}
                          className="popular-card__img"
                          loading="lazy"
                        />
                      </div>
                      <div className="popular-card__content">
                        <h4 className="popular-card__title">{item.title}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                        <div className="popular-card__bottom">
                          <span className="popular-card__price">{item.price}</span>
                          <button
                            type="button"
                            className="popular-card__add-btn"
                            onClick={(e) => fallbackCart.handleAddToCart(item, e)}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Pagination Dots */}
            {totalSlides > 1 && (
              <div className="popular-dots">
                {[...Array(totalSlides)].map((_, index) => (
                  <button
                    key={index}
                    className={`popular-dot ${currentSlide === index ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <SpecialtyCartModals cart={fallbackCart} />
    </div>
  );
};

const ApparelSpecialMenu = ({ config, menuItems, loading, nearestStore }) => {
  const { specialMenu } = config;
  const cart = useSpecialtyCart(nearestStore);
  return (
    <div id="special-menu" className="apparel-trending-section">
      <div className="container">
        <div className="apparel-trending-header">
          <h2 className="apparel-trending-title">{specialMenu.heading}</h2>
          <p className="apparel-trending-desc">{specialMenu.sub_heading}</p>
        </div>
        {loading ? (
          <p>Loading trending items...</p>
        ) : (
          <div className="apparel-trending-grid">
            {menuItems.map((item, idx) => (
              <div key={idx} className="apparel-trending-item">
                <div className="apparel-trending-img-box">
                  <VegNonVegBadge isVeg={item.isVeg} />
                  <FallbackImg src={item.image} alt={item.title} />
                  <div className="apparel-trending-overlay">
                    <button className="apparel-trending-btn" onClick={(e) => cart.handleAddToCart(item, e)}>Quick View</button>
                  </div>
                </div>
                <div className="apparel-trending-info">
                  <h4>{item.title}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                  <p>{item.description}</p>
                  <span>{item.price}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <SpecialtyCartModals cart={cart} />
    </div>
  );
};

const SweetShopSpecialMenu = ({ config, menuItems, loading, nearestStore }) => {
  const { specialMenu } = config;
  const cart = useSpecialtyCart(nearestStore);

  return (
    <div id="special-menu" className="sweet-shop-special">
      <div className="container">
        <div className="sweet-shop-special__header">
          <h2 className="sweet-shop-special__title">{specialMenu.heading}</h2>
          <div className="sweet-shop-special__divider">
            <span></span>
            <i className="fa fa-star"></i>
            <span></span>
          </div>
          <p className="sweet-shop-special__desc">{specialMenu.sub_heading}</p>
        </div>

        {loading ? (
          <div className="sweet-shop-loading">Gathering sweetness...</div>
        ) : (
          <div className="sweet-shop-special__grid">
            {menuItems.map((item, idx) => (
              <div key={idx} className="sweet-shop-special__card">
                <div className="sweet-shop-special__img-box">
                  <VegNonVegBadge isVeg={item.isVeg} />
                  <FallbackImg src={item.image} alt={item.title} />

                </div>
                <div className="sweet-shop-special__info">
                  <h4 className="sweet-shop-special__item-title">{item.title}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                  <p className="sweet-shop-special__item-desc">{item.description}</p>
                  <div className="sweet-shop-special__bottom">
                    <span className="sweet-shop-special__price">{item.price}</span>
                    <button
                      className="sweet-shop-special__order-btn"
                      onClick={(e) => cart.handleAddToCart(item, e)}
                    >
                      Order Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <SpecialtyCartModals cart={cart} />
    </div>
  );
};

const LuxurySpecialMenu = ({ config, menuItems, loading, nearestStore }) => {
  const { specialMenu } = config;
  const cart = useSpecialtyCart(nearestStore);
  return (
    <div id="special-menu" className="luxury-trending">
      <div className="container">
        <div className="luxury-trending__header">
          <h2 className="luxury-trending__title">{specialMenu.heading}</h2>
          <span className="luxury-trending__kicker">{specialMenu.sub_heading}</span>
          <div className="modern-testimonials__title-line" />
        </div>

        {loading ? (
          <div className="luxury-loading">Loading refined flavors...</div>
        ) : (
          <div className="luxury-trending__grid">
            {menuItems.map((item, idx) => (
              <div key={idx} className="luxury-trending__card">
                <div className="luxury-trending__img-box">
                  <VegNonVegBadge isVeg={item.isVeg} />
                  <FallbackImg src={item.image} alt={item.title} />
                </div>
                <div className="luxury-trending__info">
                  <h4 className="luxury-trending__item-title">{item.title}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                  <div className="luxury-trending__bottom">
                    <span className="luxury-trending__price">{item.price}</span>
                    <button className="luxury-trending__btn" onClick={(e) => cart.handleAddToCart(item, e)}>
                      <i className="fa fa-plus"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <SpecialtyCartModals cart={cart} />
    </div>
  );
};

const HeritageSpecialMenu = ({ config, menuItems, loading, nearestStore }) => {
  const { specialMenu } = config;
  const cart = useSpecialtyCart(nearestStore);

  return (
    <div id="special-menu" className="hr-special">
      <div className="container">
        <div className="hr-special__header text-center">
          <div className="hr-special__ornament">
            <span className="hr-ornament-line" />
            <span className="hr-ornament-icon">✦</span>
            <span className="hr-ornament-line" />
          </div>
          <h2 className="hr-special__title">{specialMenu.heading}</h2>
          <p className="hr-special__desc">{specialMenu.sub_heading}</p>
        </div>

        {loading ? (
          <div className="hr-special__loading">Preparing royal picks...</div>
        ) : (
          <div className="hr-special__grid">
            {menuItems.map((item, idx) => (
              <div key={idx} className="hr-special__card">
                <div className="hr-special__card-img">
                  <VegNonVegBadge isVeg={item.isVeg} />
                  <FallbackImg src={item.image} alt={item.title} />
                </div>
                <div className="hr-special__card-info">
                  <h4 className="hr-special__item-title">{item.title}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                  <p className="hr-special__item-desc">{item.description}</p>
                  <div className="hr-special__card-bottom">
                    <span className="hr-special__item-price">{item.price}</span>
                    <button className="hr-special__order-btn" onClick={(e) => cart.handleAddToCart(item, e)}>
                      Order Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <SpecialtyCartModals cart={cart} />
    </div>
  );
};

const ModernMithaiSpecialMenu = ({ config, menuItems, loading, nearestStore }) => {
  const { specialMenu } = config;
  const cart = useSpecialtyCart(nearestStore);

  return (
    <div id="special-menu" className="mm-special">
      <div className="container">
        <div className="mm-special__header text-center">
          <span className="mm-special__kicker">{specialMenu.sub_heading}</span>
          <h2 className="mm-special__title">{specialMenu.heading}</h2>
        </div>

        {loading ? (
          <div className="mm-special__loading">Curating favourites...</div>
        ) : (
          <div className="mm-special__grid">
            {menuItems.map((item, idx) => (
              <div key={idx} className="mm-special__card">
                <div className="mm-special__card-img">
                  <VegNonVegBadge isVeg={item.isVeg} />
                  <FallbackImg src={item.image} alt={item.title} />
                  <div className="mm-special__card-overlay">
                    <button className="mm-special__quick-btn" onClick={(e) => cart.handleAddToCart(item, e)}>
                      Quick Order
                    </button>
                  </div>
                </div>
                <div className="mm-special__card-info">
                  <h4 className="mm-special__item-title">{item.title}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                  <p className="mm-special__item-desc">{item.description}</p>
                  <span className="mm-special__item-price">{item.price}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <SpecialtyCartModals cart={cart} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// FESTIVE SWEETS — Ribbon-style pricing cards
// ═══════════════════════════════════════════════════════════════════
const FestiveSweetsSpecialMenu = ({ config, menuItems, loading, nearestStore }) => {
  const { specialMenu } = config;
  const cart = useSpecialtyCart(nearestStore);

  if (loading) {
    return (
      <div className="special-skeleton pad-top-120 pad-bottom-120">
        <div className="container">
          <div className="skeleton-header"></div>
          <div className="skeleton-track">
            {[1, 2, 3, 4].map(i => <div key={i} className="skeleton-circle-item"></div>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="special-menu" className="festive-carousel pad-top-120 pad-bottom-120">
      <div className="container">
        <div className="festive-carousel__header text-center mb-5">
          <h2 className="festive-carousel__title">{specialMenu.heading}</h2>
          <p className="festive-carousel__desc">{specialMenu.sub_heading}</p>
        </div>
      </div>

      <div className="festive-carousel__track-wrapper">
        <div className="festive-carousel__track">
          {/* Duplicate items for infinite effect */}
          {[...menuItems, ...menuItems].map((item, idx) => (
            <div key={idx} className="festive-charm">
              <div className="festive-charm__circle">
                <VegNonVegBadge isVeg={item.isVeg} />
                <FallbackImg src={item.image} alt={item.title} />
                <span className="festive-charm__price">{item.price}</span>
                <button
                  type="button"
                  className="festive-charm__add-btn"
                  onClick={(e) => cart.handleAddToCart(item, e)}
                  aria-label="Add to cart"
                >
                  +
                </button>
              </div>
              <h4 className="festive-charm__name">{item.title}</h4><SpiceLevel level={item.spiceLevel} size={12} />
            </div>
          ))}
        </div>
      </div>
      <SpecialtyCartModals cart={cart} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// ROYAL CONFECTIONS — Masonry grid with hover reveal
// ═══════════════════════════════════════════════════════════════════
const RoyalConfectionsSpecialMenu = ({ config, menuItems, loading, nearestStore }) => {
  const { specialMenu } = config;
  const cart = useSpecialtyCart(nearestStore);

  if (loading) {
    return (
      <div className="special-skeleton pad-top-120 pad-bottom-120">
        <div className="container">
          <div className="skeleton-grid">
            {[1, 2, 3, 4].map(i => <div key={i} className="skeleton-box-item"></div>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="special-menu" className="royal-portfolio pad-top-120 pad-bottom-120">
      <div className="container">
        <div className="royal-portfolio__header mb-5">
          <div className="row align-items-end">
            <div className="col-md-6">
              <h2 className="royal-portfolio__title">{specialMenu.heading}</h2>
            </div>
            <div className="col-md-6 text-md-right">
              <p className="royal-portfolio__desc">{specialMenu.sub_heading}</p>
            </div>
          </div>
        </div>

        <div className="royal-portfolio__grid">
          {menuItems.map((item, idx) => (
            <div key={idx} className="royal-asset">
              <div className="royal-asset__index">0{idx + 1}</div>
              <div className="royal-asset__img-box">
                <VegNonVegBadge isVeg={item.isVeg} />
                <FallbackImg src={item.image} alt={item.title} />
                <div className="royal-asset__overlay">
                  <button type="button" className="royal-asset__btn" onClick={(e) => cart.handleAddToCart(item, e)}>ENQUIRY NOW</button>
                </div>
              </div>
              <div className="royal-asset__details">
                <h4 className="royal-asset__name">{item.title}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                <div className="royal-asset__price">{item.price}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <SpecialtyCartModals cart={cart} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TRADITIONAL SWEETS — Horizontal cards with circular images
// ═══════════════════════════════════════════════════════════════════
const TraditionalSweetsSpecialMenu = ({ config, menuItems, loading, nearestStore }) => {
  const { specialMenu } = config;
  const cart = useSpecialtyCart(nearestStore);

  return (
    <div id="special-menu" className="ts-special">
      <div className="container">
        <div className="ts-special__header text-center">
          <div className="ts-special__badge">{specialMenu.sub_heading}</div>
          <h2 className="ts-special__title">{specialMenu.heading}</h2>
          <p className="ts-special__desc">{specialMenu.sub_heading}</p>
        </div>

        {loading ? (
          <div className="ts-special__loading">Fetching fresh sweets...</div>
        ) : (
          <div className="ts-special__grid">
            {menuItems.map((item, idx) => (
              <div key={idx} className="ts-special__card">
                <div className="ts-special__img-box">
                  <VegNonVegBadge isVeg={item.isVeg} />
                  <FallbackImg src={item.image} alt={item.title} />
                </div>
                <div className="ts-special__info">
                  <div className="ts-special__top">
                    <h4 className="ts-special__item-title">{item.title}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                    <span className="ts-special__price">{item.price}</span>
                  </div>
                  <p className="ts-special__item-desc">{item.description}</p>
                  <button className="ts-special__btn" onClick={(e) => cart.handleAddToCart(item, e)}>
                    Pick & Box it
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <SpecialtyCartModals cart={cart} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TEMPLATE 16 — ARISA COLLECTION — "Best Sellers"
// Editorial product grid, restrained hover-reveal CTA.
// ═══════════════════════════════════════════════════════════════════
const SweetStoreSpecialMenu = ({ config, menuItems, loading, nearestStore }) => {
  const { specialMenu } = config;
  const navigate = useNavigate();
  const { selectedStore: contextStore, orderType, isPreOrder, setStore, setPreOrder } = useOrderFlow();
  const effectiveOrderType = orderType || 'delivery';
  const effectiveStore = contextStore || nearestStore;

  const getStoreClosedStatus = () => {
    if (!effectiveStore?.week_schedule) return { isClosed: false, message: '' };
    const availability = checkStoreAvailability(effectiveStore.week_schedule);
    const supportsPreOrder = Boolean(effectiveStore?.allow_pre_order || effectiveStore?.pre_order_enabled);
    const isClosed = Boolean(availability && !availability.isOpen && !isPreOrder && !supportsPreOrder);
    return { isClosed, message: availability?.message || 'Store is currently closed' };
  };

  const [openWeightFor, setOpenWeightFor] = useState(null);
  const [justAdded, setJustAdded] = useState(null);
  const [selectedWeightByItem, setSelectedWeightByItem] = useState({});
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showStoreClosedModal, setShowStoreClosedModal] = useState(false);
  const pendingActionRef = useRef(null);
  const dropdownRef = useRef(null);
  // Only used to force a re-render when liveCartQuantities (module-level,
  // see above) changes — the map itself lives outside component state.
  const [, forceCartQuantityRerender] = useState(0);

  // This card does NOT read the cart on mount/page load — showing a
  // quantity for an item that's merely left over in localStorage from a
  // much earlier visit, the instant a fresh visit loads, is confusing (that
  // was the original reason this stayed disconnected from live cart state).
  // It DOES track live quantity from here on: once a cart_updated/storage
  // event actually happens (from this card, the Restaurant Menu page, or
  // another tab), the affected card's displayed quantity updates and stays
  // in sync for the rest of this browsing session — see liveCartQuantities.
  useEffect(() => {
    if (!effectiveStore?.id) return;
    lastCartContext = { storeId: effectiveStore.id, orderType: effectiveOrderType };
    ensureLiveCartListeners();

    const onCartEvent = () => forceCartQuantityRerender(v => v + 1);
    window.addEventListener('cart_updated', onCartEvent);
    window.addEventListener('storage', onCartEvent);
    return () => {
      window.removeEventListener('cart_updated', onCartEvent);
      window.removeEventListener('storage', onCartEvent);
    };
  }, [effectiveStore?.id, effectiveOrderType]);

  // Close the open weight dropdown on outside click.
  useEffect(() => {
    if (openWeightFor === null) return;
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenWeightFor(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openWeightFor]);

  const persistCartItem = (cartItem) => {
    if (!effectiveStore?.id) return;

    // Make this store the app-wide selected store so the shop page resolves
    // the exact same cart key this card just wrote to.
    if (!contextStore || contextStore.id !== effectiveStore.id) {
      setStore(effectiveStore);
    }

    const key = getCartKey(effectiveStore.id, effectiveOrderType);
    let existingItems = [];
    try {
      const stored = JSON.parse(localStorage.getItem(key) || 'null');
      existingItems = stored?.items || [];
    } catch {
      existingItems = [];
    }

    const idx = existingItems.findIndex(it => it.cartItemKey === cartItem.cartItemKey);
    let updatedItems;
    if (idx >= 0) {
      updatedItems = [...existingItems];
      updatedItems[idx] = { ...updatedItems[idx], quantity: (updatedItems[idx].quantity || 1) + 1 };
    } else {
      updatedItems = [...existingItems, cartItem];
    }

    localStorage.setItem(key, JSON.stringify({
      items: updatedItems,
      timestamp: Date.now(),
      storeId: effectiveStore.id,
      orderType: effectiveOrderType
    }));
    window.dispatchEvent(new Event('cart_updated'));

    setJustAdded(cartItem.cartItemKey);
    setTimeout(() => setJustAdded(current => (current === cartItem.cartItemKey ? null : current)), 1500);
  };

  // Same storage key/shape and cart_updated event as persistCartItem above —
  // just the +/- side of it, for the quantity stepper. Mirrors
  // RestaurantMenuApp's own updateCartQuantity: dropping to 0 removes the
  // line entirely rather than keeping a quantity:0 row, so a cart written by
  // either surface stays interchangeable.
  const updateCartItemQuantity = (cartItemKey, delta) => {
    if (!effectiveStore?.id) return;
    if (delta > 0) {
      const { isClosed } = getStoreClosedStatus();
      if (isClosed) {
        setShowStoreClosedModal(true);
        return;
      }
    }
    const key = getCartKey(effectiveStore.id, effectiveOrderType);
    let existingItems = [];
    try {
      const stored = JSON.parse(localStorage.getItem(key) || 'null');
      existingItems = stored?.items || [];
    } catch {
      existingItems = [];
    }

    const idx = existingItems.findIndex(it => it.cartItemKey === cartItemKey);
    if (idx === -1) return;

    const newQuantity = (existingItems[idx].quantity || 1) + delta;
    const updatedItems = newQuantity > 0
      ? existingItems.map((it, i) => (i === idx ? { ...it, quantity: newQuantity } : it))
      : existingItems.filter((_, i) => i !== idx);

    localStorage.setItem(key, JSON.stringify({
      items: updatedItems,
      timestamp: Date.now(),
      storeId: effectiveStore.id,
      orderType: effectiveOrderType
    }));
    window.dispatchEvent(new Event('cart_updated'));
  };

  // Same login requirement the Restaurant Menu page enforces before adding
  // to cart (RestaurantMenuApp's addToCart checks getCustomerId() first) —
  // this homepage card never had that check, so unauthenticated visitors
  // could add here but not there. Reuses the app's own auth session check
  // and AuthModal component; no new auth logic.
  const isLoggedIn = () => !!getValidAuthSession()?.userId;

  const addSimpleItem = (item) => {
    persistCartItem({
      id: item.id,
      name: item.title,
      price: item.priceValue,
      flatPrice: item.priceValue,
      image: item.image,
      description: item.description || '',
      quantity: 1,
      variations: [],
      extras: [],
      addons: [],
      cartItemKey: String(item.id),
      is_tracked: item.isTracked,
      is_available: item.isAvailable,
      category: item.categoryName
    });
  };

  const addWeightedItem = (item, option) => {
    const calculatedPrice = option.value * item.priceValue;
    persistCartItem({
      id: item.id,
      name: item.title,
      image: item.image,
      description: item.description || '',
      selectedWeight: option.value,
      weightUnit: item.weightUnitLabel,
      flatPrice: calculatedPrice,
      price: calculatedPrice,
      isWeightBased: true,
      weightLabel: option.label,
      quantity: 1,
      variations: [],
      extras: [],
      addons: [],
      cartItemKey: `${item.id}|w:${option.value}`,
      category: item.categoryName
    });
    setSelectedWeightByItem(prev => ({ ...prev, [item.id]: option }));
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    // The Header's logged-in display reads from App.js's own userToken/
    // userId state, which only App.js's own login flow updates. This modal
    // logs in successfully (the session cookie is set) but App.js has no
    // way to know that happened, so the Header kept showing "Login" —
    // notify it the same way cart changes already notify other components.
    window.dispatchEvent(new Event('auth_updated'));
    const pending = pendingActionRef.current;
    pendingActionRef.current = null;
    if (!pending) return;
    if (pending.type === 'simple') addSimpleItem(pending.item);
    else if (pending.type === 'weighted') addWeightedItem(pending.item, pending.option);
  };

  const handleAddToCart = (item, e) => {
    e.stopPropagation();
    if (!effectiveStore?.id) {
      // No store resolved yet anywhere in the app — fall back to the shop
      // page, same as before, rather than silently failing.
      navigate('/restaurants');
      return;
    }
    const { isClosed } = getStoreClosedStatus();
    if (isClosed) {
      setShowStoreClosedModal(true);
      return;
    }
    if (item.isWeightBased) {
      setOpenWeightFor(prev => (prev === item.id ? null : item.id));
      return;
    }
    if (!isLoggedIn()) {
      pendingActionRef.current = { type: 'simple', item };
      setShowAuthModal(true);
      return;
    }
    addSimpleItem(item);
  };

  const handleSelectWeight = (item, option, e) => {
    e.stopPropagation();
    setOpenWeightFor(null);
    const { isClosed } = getStoreClosedStatus();
    if (isClosed) {
      setShowStoreClosedModal(true);
      return;
    }
    if (!isLoggedIn()) {
      pendingActionRef.current = { type: 'weighted', item, option };
      setShowAuthModal(true);
      return;
    }
    addWeightedItem(item, option);
  };

  return (
    <div id="special-menu" className="t16-bestsellers">
      <div className="t16-bestsellers__container">
        <div className="t16-bestsellers__header">
          <span className="t16-bestsellers__kicker">{specialMenu?.sub_heading || 'Customer Favourites'}</span>
          <h2 className="t16-bestsellers__title">{specialMenu?.heading || 'Best Sellers'}</h2>
        </div>

        {loading ? (
          <div className="t16-bestsellers__loading">Loading favourites...</div>
        ) : (
          <div className="t16-bestsellers__grid">
            {menuItems.map((item, idx) => {
              const simpleKey = String(item.id);
              const selectedWeight = selectedWeightByItem[item.id];
              // selectedWeightByItem is local state — it resets to {} whenever
              // this component remounts (e.g. navigating to the Restaurant
              // Menu App and back), which used to make a weight-based item
              // that's genuinely already in the cart show "Choose Weight"
              // again, because activeCartKey below had no way to know which
              // weight it was without an explicit pick in THIS mount. Fall
              // back to whichever of this item's weight variants already has
              // a live quantity (from liveCartQuantities — event-driven, not
              // an eager mount-time read, so this still stays empty on a
              // genuinely fresh visit) so a remount restores it correctly.
              const restoredWeight = item.isWeightBased && !selectedWeight
                ? item.weightOptions.find(opt => (liveCartQuantities[`${item.id}|w:${opt.value}`] || 0) > 0)
                : null;
              const effectiveSelectedWeight = selectedWeight || restoredWeight || null;
              // activeCartKey is the ONE variant "-" acts on (the currently
              // selected/restored weight) — see updateCartItemQuantity below.
              // It is NOT the same as "how much of this item is in the cart",
              // because a weight-based item can have several DIFFERENT
              // weight variants in the cart at once (e.g. 250g and 500g are
              // separate cartItemKeys, per generateCartItemKey's own
              // `${id}|w:${weight}` scheme — same one Restaurant Menu App
              // uses), not one combined quantity. cartWeightBreakdown below
              // reads every variant independently rather than collapsing
              // them into the single activeCartKey.
              const activeCartKey = item.isWeightBased
                ? (effectiveSelectedWeight ? `${item.id}|w:${effectiveSelectedWeight.value}` : null)
                : simpleKey;
              // All weight variants of this item currently in the cart, each
              // read independently by its own `${id}|w:${weight}` key — only
              // ever derived from real cart_updated/storage events (see
              // liveCartQuantities above), never an eager mount-time read.
              const cartWeightBreakdown = item.isWeightBased
                ? item.weightOptions
                  .map(opt => ({ option: opt, qty: liveCartQuantities[`${item.id}|w:${opt.value}`] || 0 }))
                  .filter(w => w.qty > 0)
                : [];
              const liveQuantity = item.isWeightBased
                ? cartWeightBreakdown.reduce((sum, w) => sum + w.qty, 0)
                : (liveCartQuantities[simpleKey] || 0);
              // The price line must always match whatever weight the pill is
              // currently showing (defaulting to the first option before an
              // explicit pick), not the flat per-unit base price.
              const displayWeightOption = item.isWeightBased ? (effectiveSelectedWeight || item.weightOptions[0]) : null;
              const displayPrice = displayWeightOption
                ? settingsService.formatPrice(displayWeightOption.value * item.priceValue)
                : item.price;
              // Kept simple by design: just the selected/default weight, not
              // a per-variant breakdown (e.g. "250g x3 + 500g x2 + 1kg") —
              // the cart itself still tracks every variant independently
              // (see cartWeightBreakdown, used for the total below), this is
              // only about what the card displays.
              const weightPillLabel = effectiveSelectedWeight?.label || item.weightOptions[0]?.label || 'Select weight';

              return (
                <div key={idx} className="t16-bestsellers__card" onClick={() => navigate('/restaurants')}>
                  <div className="t16-bestsellers__img-box">
                    <VegNonVegBadge isVeg={item.isVeg} />
                    <FallbackImg src={item.image} alt={item.title} />
                  </div>
                  <div className="t16-bestsellers__info">
                    <h4 className="t16-bestsellers__item-title">{item.title}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                    {item.categoryName && (
                      <span className="t16-bestsellers__category">{item.categoryName}</span>
                    )}

                    {item.isWeightBased && (
                      <div className="t16-bestsellers__weight-wrap" ref={openWeightFor === item.id ? dropdownRef : null}>
                        <span
                          className="t16-bestsellers__weight"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); setOpenWeightFor(prev => (prev === item.id ? null : item.id)); }}
                        >
                          {weightPillLabel}
                        </span>
                        {openWeightFor === item.id && (
                          <div className="t16-bestsellers__weight-dropdown" onClick={(e) => e.stopPropagation()}>
                            {item.weightOptions.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                className="t16-bestsellers__weight-option"
                                onClick={(e) => handleSelectWeight(item, option, e)}
                              >
                                <span>{option.label}</span>
                                <span>{settingsService.formatPrice(option.value * item.priceValue)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <span className="t16-bestsellers__price">{displayPrice}</span>

                    {justAdded && justAdded === activeCartKey ? (
                      <button type="button" className="t16-bestsellers__add-btn added" disabled>
                        ✓ Added to Cart
                      </button>
                    ) : liveQuantity > 0 ? (
                      <div className="t16-bestsellers__qty-stepper" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="t16-bestsellers__qty-btn"
                          aria-label="Decrease quantity"
                          onClick={() => updateCartItemQuantity(activeCartKey, -1)}
                        >
                          −
                        </button>
                        <span className="t16-bestsellers__qty-value">{liveQuantity}</span>
                        <button
                          type="button"
                          className="t16-bestsellers__qty-btn"
                          aria-label={item.isWeightBased ? 'Add another weight' : 'Increase quantity'}
                          onClick={() => {
                            if (item.isWeightBased) {
                              // A weight-based item can have several
                              // different weight variants in the cart at
                              // once (see cartWeightBreakdown) — "+" can't
                              // know which one to bump, so it opens the same
                              // weight picker "Choose Weight" itself uses,
                              // letting the user add the same weight again
                              // (increments it, same as persistCartItem
                              // already does for an existing cartItemKey) or
                              // a different one (adds a new variant).
                              setOpenWeightFor(item.id);
                            } else {
                              updateCartItemQuantity(activeCartKey, 1);
                            }
                          }}
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="t16-bestsellers__add-btn"
                        onClick={(e) => handleAddToCart(item, e)}
                      >
                        {item.isWeightBased ? 'Choose Weight' : 'Add to Cart'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => { setShowAuthModal(false); pendingActionRef.current = null; }}
        onAuthSuccess={handleAuthSuccess}
        branchId={effectiveStore?.id}
      />
      <StoreClosedModal
        isOpen={showStoreClosedModal}
        onClose={() => setShowStoreClosedModal(false)}
        store={{
          ...effectiveStore,
          name: effectiveStore?.name || effectiveStore?.branch_name || 'Store',
          location: effectiveStore?.location || [effectiveStore?.address, effectiveStore?.city, effectiveStore?.state].filter(Boolean).join(', ') || 'Store Location'
        }}
        storeStatus={effectiveStore?.week_schedule ? getStoreStatus(effectiveStore.week_schedule) : null}
        onPreOrder={() => {
          setShowStoreClosedModal(false);
          setPreOrder(true);
          navigate('/restaurants');
        }}
        supportsPreOrder={Boolean(effectiveStore?.allow_pre_order || effectiveStore?.pre_order_enabled)}
        hasOtherStores={false}
      />
    </div>
  );
};

const LegacyAshaSpecialMenu = ({ config, menuItems, loading, nearestStore }) => {
  const { specialMenu } = config;
  const cart = useSpecialtyCart(nearestStore);
  const [activeIndex, setActiveIndex] = useState(0);

  // Auto-advance carousel
  useEffect(() => {
    if (!menuItems || menuItems.length === 0) return;
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % menuItems.length);
    }, 4000); // 4 seconds
    return () => clearInterval(interval);
  }, [menuItems]);


  if (loading) {
    return (
      <div className="special-skeleton pad-top-120 pad-bottom-120">
        <div className="container">
          <div className="row">
            <div className="col-lg-6"><div className="skeleton-circle-large"></div></div>
            <div className="col-lg-6"><div className="skeleton-text-block"></div></div>
          </div>
        </div>
      </div>
    );
  }
  if (!menuItems.length) return null;

  const currentItem = menuItems[activeIndex];

  return (
    <div id="special-menu" className="asha-spotlight pad-top-120 pad-bottom-120">
      <div className="container">
        <div className="asha-spotlight__header text-center mb-5">
          <h2 className="asha-spotlight__title">{specialMenu.heading}</h2>
          <span className="asha-spotlight__kicker">{specialMenu.sub_heading}</span>
          <div className="modern-testimonials__title-line" />

        </div>

        <div className="asha-spotlight__main">
          <div className="row align-items-center">
            {/* Left: Large Rotating Circle */}
            <div className="col-lg-6">
              <div className="asha-spotlight__visual">
                <div className="asha-circle-glow"></div>
                <div className="asha-circle-main">
                  <VegNonVegBadge isVeg={currentItem.isVeg} />
                  <FallbackImg
                    key={activeIndex}
                    src={currentItem.image}
                    alt={currentItem.title}
                    className="asha-spotlight__img"
                  />
                </div>
                <div className="asha-rotating-ring"></div>
              </div>
            </div>

            {/* Right: Item Details */}
            <div className="col-lg-6">
              <div className="asha-spotlight__content">
                <div className="asha-spotlight__info" key={activeIndex}>
                  <h3 className="asha-spotlight__item-title">{currentItem.title}</h3><SpiceLevel level={currentItem.spiceLevel} size={12} />
                  <div className="asha-spotlight__price">{currentItem.price}</div>
                  <p className="asha-spotlight__item-desc">{currentItem.description}</p>
                  <button className="asha-spotlight__btn" onClick={(e) => cart.handleAddToCart(currentItem, e)}>
                    Order Now
                    <i className="fa fa-long-arrow-right" />
                  </button>
                </div>

                {/* Thumbnails Strip */}
                <div className="asha-spotlight__thumbs">
                  {menuItems.map((item, idx) => (
                    <div
                      key={idx}
                      className={`asha-thumb ${idx === activeIndex ? 'is-active' : ''}`}
                      onClick={() => setActiveIndex(idx)}
                    >
                      <FallbackImg src={item.image} alt={item.title} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SpecialtyCartModals cart={cart} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// CLASSIC SPECIAL MENU — Minimalist grid with floating price and hover
// ═══════════════════════════════════════════════════════════════════
const ClassicSpecialMenu = ({ config, menuItems, loading, nearestStore }) => {
  const { specialMenu } = config;
  const cart = useSpecialtyCart(nearestStore);

  return (
    <div id="special-menu" className="classic-special pad-top-120 pad-bottom-120">
      <div className="container">
        <div className="classic-special__header text-center mb-5">
          <h2 className="classic-special__title">{specialMenu.heading}</h2>
          <p className="classic-special__desc">{specialMenu.sub_heading}</p>
          <div className="modern-testimonials__title-line" />

        </div>
        {loading ? (
          <div className="classic-loading">Loading excellence...</div>
        ) : (
          <div className="classic-special__grid">
            {menuItems.map((item, idx) => (
              <div key={idx} className="classic-item">
                <div className="classic-item__img-box">
                  <VegNonVegBadge isVeg={item.isVeg} />
                  <FallbackImg src={item.image} alt={item.title} />
                  <span className="classic-item__price">{item.price}</span>
                </div>
                <div className="classic-item__info">
                  <h4>{item.title}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                  <p>{item.description}</p>
                  <button type="button" className="classic-item__add-btn" onClick={(e) => cart.handleAddToCart(item, e)}>Add</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <SpecialtyCartModals cart={cart} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TRADITIONAL SPECIAL MENU — Horizontal Heritage Cards
// ═══════════════════════════════════════════════════════════════════
const TraditionalSpecialMenu = ({ config, menuItems, loading, nearestStore }) => {
  const { specialMenu } = config;
  const navigate = useNavigate();
  const cart = useSpecialtyCart(nearestStore);

  return (
    <div id="special-menu" className="trad-special">
      <div className="container">
        <div className="trad-special__header text-center">
          <div className="trad-special__badge">{specialMenu.sub_heading}</div>
          <h2 className="trad-special__title">{specialMenu.heading}</h2>
          <div className="trad-special__divider">
            <span className="trad-special__line"></span>
            <span className="trad-special__diamond"></span>
            <span className="trad-special__line"></span>
          </div>
        </div>
        {loading ? (
          <div className="trad-loading">Gathering traditions...</div>
        ) : (
          <div className="trad-special__grid">
            {menuItems.map((item, idx) => (
              <div key={idx} className="trad-item" onClick={() => navigate('/restaurants')}>
                <div className="trad-item__img-box">
                  <VegNonVegBadge isVeg={item.isVeg} />
                  <FallbackImg src={item.image} alt={item.title} className="trad-item__img" />
                </div>
                <div className="trad-item__details">
                  <div className="trad-item__price">{item.price}</div>
                  <h4 className="trad-item__title">{item.title}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                  <p className="trad-item__desc">{item.description}</p>
                  <button className="trad-item__btn" onClick={(e) => cart.handleAddToCart(item, e)}>Order Now</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <SpecialtyCartModals cart={cart} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// SUNBURST SPECIAL MENU — Horizontal Snap Scroll (L to R)
// ═══════════════════════════════════════════════════════════════════
const SunburstSpecialMenu = ({ config, menuItems, loading, nearestStore }) => {
  const { specialMenu } = config;
  const cart = useSpecialtyCart(nearestStore);

  return (
    <div id="special-menu" className="ssb-special pad-top-120 pad-bottom-120">
      <div className="container">
        <div className="ssb-special__header d-flex align-items-end justify-content-between mb-5">
          <div>
            <h2 className="ssb-special__title">{specialMenu.heading}</h2>
            <p className="ssb-special__desc">{specialMenu.sub_heading}</p>
          </div>
          <div className="ssb-special__scroll-hint">
            Scroll for more <i className="fa fa-long-arrow-right" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="container">Loading gourmet...</div>
      ) : (
        <div className="ssb-special__scroll-container">
          <div className="ssb-special__track">
            {menuItems.map((item, idx) => (
              <div key={idx} className="ssb-special__card">
                <div className="ssb-special__img-box">
                  <VegNonVegBadge isVeg={item.isVeg} />
                  <FallbackImg src={item.image} alt={item.title} />
                </div>
                <div className="ssb-special__info">
                  <h4 className="ssb-special__item-title">{item.title}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                  <p className="ssb-special__item-desc">{item.description}</p>
                  <div className="ssb-special__footer">
                    <span className="ssb-special__price">{item.price}</span>
                    <button
                      type="button"
                      className="ssb-special__add-btn"
                      onClick={(e) => cart.handleAddToCart(item, e)}
                      aria-label="Add to cart"
                    >
                      <i className="fa fa-shopping-cart" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <SpecialtyCartModals cart={cart} />
    </div>
  );
};
// ═══════════════════════════════════════════════════════════════════
// CAFÉ PREMIUM — Popular Brews horizontal scroll showcase
// ═══════════════════════════════════════════════════════════════════
const CafeSpecialMenu = ({ config, menuItems, loading, nearestStore }) => {
  const { specialMenu } = config;
  const navigate = useNavigate();
  const cart = useSpecialtyCart(nearestStore);

  return (
    <div id="special-menu" className="cafe-special pad-top-100 pad-bottom-100">
      <div className="container">
        <div className="cafe-special__header text-center mb-5">
          <span className="cafe-special__kicker">{specialMenu.sub_heading}</span>
          <h2 className="cafe-special__title">{specialMenu.heading}</h2>
        </div>
      </div>

      {loading ? (
        <div className="container">Brewing your picks...</div>
      ) : (
        <div className="cafe-special__scroll">
          <div className="cafe-special__track">
            {menuItems.map((item, idx) => (
              <div key={idx} className="cafe-special__card" onClick={() => navigate('/restaurants')}>
                <div className="cafe-special__img-box">
                  <VegNonVegBadge isVeg={item.isVeg} />
                  <FallbackImg src={item.image} alt={item.title} />
                  <div className="cafe-special__price-tag">{item.price}</div>
                </div>
                <div className="cafe-special__info">
                  <h4 className="cafe-special__item-title">{item.title}</h4><SpiceLevel level={item.spiceLevel} size={12} />
                  <p className="cafe-special__item-desc">{item.description}</p>
                  <button className="cafe-special__order-btn" onClick={(e) => cart.handleAddToCart(item, e)}>
                    <i className="fa fa-shopping-bag" style={{ marginRight: '6px' }} /> Order
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <SpecialtyCartModals cart={cart} />
    </div>
  );
};

export default SpecialMenu;
